import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Download,
  TrendingUp,
  Clock,
  Shield,
  CheckCircle,
  AlertTriangle,
  Building,
  MapPin,
  X,
  ExternalLink,
} from "lucide-react";
import ComparisonMatrix from "../components/ComparisonMatrix";
import RoadmapBoard from "../components/RoadmapBoard";
import MatchBreakdown from "../components/MatchBreakdown";
import "./Results.css";

function Results({ analysisResult }) {
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  // JOB STATES
  const [visibleJobCount, setVisibleJobCount] = useState(6);
  const [selectedJob, setSelectedJob] = useState(null); // For Modal
  const [showMatrixModal, setShowMatrixModal] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (analysisResult?.recommended_countries?.length > 0) {
      setSelectedCountry(analysisResult.recommended_countries[0]);
    }
  }, [analysisResult]);

  useEffect(() => {
    setVisibleJobCount(6);
  }, [selectedCountry]);

  if (!analysisResult) return null;

  const { recommended_countries, comparison_matrix } = analysisResult;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/analysis/export-pdf`,
        analysisResult,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "Atlas_Strategy_Dossier.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("PDF Export failed", error);
      alert("Could not generate dossier.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="results-dashboard">
      <div className="dashboard-header">
        <button onClick={() => navigate("/analyze")} className="back-btn">
          <ArrowLeft size={18} /> New Analysis
        </button>
        <div className="header-title">
          <h1>Migration Strategy</h1>
          <p>
            Generated for {analysisResult.user_profile.nationality} Citizens
          </p>
        </div>
        <button
          onClick={handleDownload}
          className="download-btn"
          disabled={downloading}
        >
          <Download size={18} />{" "}
          {downloading ? "Generating..." : "Export Dossier"}
        </button>
      </div>

      <div className="dashboard-grid">
        {/* LEFT SIDEBAR - FULL LIST (No Slice) */}
        <div className="sidebar-list">
          <h3>Top Recommendations</h3>
          <div className="country-list">
            {recommended_countries.map((country, idx) => (
              <div
                key={country.code}
                className={`list-item ${
                  selectedCountry?.code === country.code ? "active" : ""
                }`}
                onClick={() => setSelectedCountry(country)}
              >
                <div className="rank-number">#{idx + 1}</div>
                <div className="item-info">
                  <h4>{country.name}</h4>
                  <span className="visa-tag">
                    {country.open_jobs?.length || 0} Open Roles
                  </span>
                </div>
                <div className="match-score">
                  {Math.round(country.match_score)}%
                </div>
              </div>
            ))}
          </div>

          <div className="sidebar-tools">
            <button
              className="tool-btn"
              onClick={() => setShowMatrixModal(true)}
            >
              Compare All (Matrix)
            </button>
          </div>
        </div>

        {/* RIGHT MAIN PANEL */}
        <div className="main-panel">
          {selectedCountry && (
            <>
              {/* HERO SECTION */}
              <div className="country-hero">
                <div className="hero-header">
                  <h2>{selectedCountry.name}</h2>
                  <div className="tags">
                    <span className="tag savings">
                      <TrendingUp size={14} /> Save $
                      {selectedCountry.cost_comparison.savings.toLocaleString()}{" "}
                      / 5yr
                    </span>
                    <span className="tag speed">
                      <Clock size={14} /> {selectedCountry.processing_time_days}{" "}
                      Day Process
                    </span>
                  </div>
                </div>

                <div className="hero-stats">
                  <div className="stat-box">
                    <label>Monthly Cost</label>
                    <div className="val">
                      ${selectedCountry.cost_of_living_monthly}
                    </div>
                  </div>
                  <div className="stat-box">
                    <label>Tax Rate</label>
                    <div className="val">
                      {selectedCountry.tax_rate_percentage}%
                    </div>
                  </div>
                  <div className="stat-box">
                    <label>Tech Ecosystem</label>
                    <div className="val">
                      {selectedCountry.open_jobs?.length > 5
                        ? "Strong"
                        : "Emerging"}
                    </div>
                  </div>
                  <div className="stat-box">
                    <label>Safety</label>
                    <div className="val">{selectedCountry.safety_score}/10</div>
                  </div>
                </div>

                {/* TABS */}
                <div className="panel-tabs">
                  <button
                    className={activeTab === "overview" ? "active" : ""}
                    onClick={() => setActiveTab("overview")}
                  >
                    Overview
                  </button>
                  <button
                    className={activeTab === "jobs" ? "active" : ""}
                    onClick={() => setActiveTab("jobs")}
                  >
                    Job Market{" "}
                    <span className="badge">
                      {selectedCountry.open_jobs?.length}
                    </span>
                  </button>
                  <button
                    className={activeTab === "roadmap" ? "active" : ""}
                    onClick={() => setActiveTab("roadmap")}
                  >
                    Execution Roadmap
                  </button>
                </div>
              </div>

              {/* OVERVIEW CONTENT */}
              {activeTab === "overview" && (
                <div className="details-grid">
                  <div className="grid-col">
                    <MatchBreakdown
                      country={selectedCountry}
                      profile={analysisResult.user_profile}
                    />
                    <div className="detail-card">
                      <div className="card-header">
                        <Shield size={18} /> Visa Strategy
                      </div>
                      <div className="card-row">
                        <span>Type</span>
                        <strong>{selectedCountry.visa_type}</strong>
                      </div>
                      <div className="card-row">
                        <span>Min. Income</span>
                        <strong>
                          ${selectedCountry.min_income_requirement}/mo
                        </strong>
                      </div>
                      <div className="card-row">
                        <span>Visa Cost</span>
                        <strong>${selectedCountry.visa_cost}</strong>
                      </div>
                      <div className="card-row">
                        <span>Duration</span>
                        <strong>
                          {selectedCountry.duration_months} Months
                        </strong>
                      </div>
                    </div>
                  </div>
                  <div className="grid-col">
                    <div className="detail-card">
                      <div className="card-header">
                        <CheckCircle size={18} /> Why {selectedCountry.name}?
                      </div>
                      <ul className="pros-list">
                        {analysisResult.user_profile.skills
                          .slice(0, 1)
                          .map((skill) => (
                            <li key={skill}>
                              <CheckCircle size={14} className="icon-good" />{" "}
                              Your <strong>{skill}</strong> skills are in high
                              demand.
                            </li>
                          ))}
                        <li>
                          <CheckCircle size={14} className="icon-good" />{" "}
                          {selectedCountry.tax_rate_percentage === 0
                            ? "Tax Free Income"
                            : "Competitive Tax Treaty"}
                        </li>
                        <li>
                          <CheckCircle size={14} className="icon-good" />{" "}
                          {selectedCountry.internet_speed_mbps} Mbps Avg
                          Internet
                        </li>
                        <li>
                          <CheckCircle size={14} className="icon-good" />{" "}
                          {selectedCountry.safety_score > 8
                            ? "Top-tier safety rating"
                            : "Stable environment"}
                        </li>
                      </ul>
                      <div className="warning-box">
                        <AlertTriangle size={16} />
                        <span>
                          Bureaucracy:{" "}
                          {selectedCountry.processing_time_days > 45
                            ? "High (Expect Delays)"
                            : "Low (Digital Process)"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* JOBS CONTENT */}
              {activeTab === "jobs" && (
                <div className="jobs-container">
                  <h3 className="section-title">
                    Verified Opportunities for{" "}
                    {analysisResult.user_profile.occupation}s
                  </h3>
                  <div className="jobs-grid">
                    {selectedCountry.open_jobs &&
                    selectedCountry.open_jobs.length > 0 ? (
                      selectedCountry.open_jobs
                        .slice(0, visibleJobCount)
                        .map((job) => (
                          <div key={job.id} className="job-card">
                            <div className="job-card-content">
                              <div className="job-header">
                                <div className="company-icon">
                                  <Building size={20} />
                                </div>
                                <div className="job-main-info">
                                  <h4>{job.title}</h4>
                                  <span className="company-name">
                                    {job.company}
                                  </span>
                                </div>
                              </div>

                              <div className="job-meta">
                                <span>
                                  <MapPin size={14} /> {job.location}
                                </span>
                                <span className="salary">
                                  {job.salary_range}
                                </span>
                              </div>

                              <div className="job-tags">
                                {job.tags.slice(0, 2).map((tag) => (
                                  <span key={tag} className="job-tag">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </div>

                            <button
                              className="apply-btn"
                              onClick={() => setSelectedJob(job)}
                            >
                              View Description
                            </button>
                          </div>
                        ))
                    ) : (
                      <div className="no-jobs">
                        <p>No direct matches found in our network.</p>
                      </div>
                    )}
                  </div>

                  {selectedCountry.open_jobs &&
                    selectedCountry.open_jobs.length > visibleJobCount && (
                      <div className="load-more-container">
                        <button
                          className="load-more-btn"
                          onClick={() => setVisibleJobCount((prev) => prev + 6)}
                        >
                          Load More Opportunities
                        </button>
                      </div>
                    )}
                </div>
              )}

              {/* ROADMAP CONTENT */}
              {activeTab === "roadmap" && (
                <RoadmapBoard tasks={selectedCountry.roadmap || []} />
              )}
            </>
          )}
        </div>
      </div>

      {/* MODALS (Job & Matrix) */}
      {selectedJob && (
        <div className="modal-overlay" onClick={() => setSelectedJob(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedJob.title}</h2>
              <button
                className="close-btn"
                onClick={() => setSelectedJob(null)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-sub-header">
              <span>
                <Building size={16} /> {selectedJob.company}
              </span>
              <span>
                <MapPin size={16} /> {selectedJob.location}
              </span>
            </div>
            <div className="modal-body">
              <div
                dangerouslySetInnerHTML={{ __html: selectedJob.description }}
              />
            </div>
            <div className="modal-footer">
              <a
                href={selectedJob.link}
                target="_blank"
                rel="noreferrer"
                className="modal-apply-btn"
              >
                Apply on Company Site <ExternalLink size={16} />
              </a>
            </div>
          </div>
        </div>
      )}

      {showMatrixModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowMatrixModal(false)}
        >
          <div
            className="modal-content large"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Global Comparison Matrix</h2>
              <button
                className="close-btn"
                onClick={() => setShowMatrixModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <ComparisonMatrix data={comparison_matrix} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Results;